import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Icon from '@components/core/Icon';
import ActionDropdown from '@components/core/ActionDropdown';
import './RecruitmentFlowBar.less';

/**
 * Clean recruitment step slider. No store/API dependencies.
 * Props:
 *   steps  – array of { id, name }
 *   value  – currently selected step id
 *   onChange(id) – called when user clicks a step
 */
const RecruitmentFlowBar = ({ steps = [], value, onChange }) => {
    const [isMobile, setIsMobile] = useState(false);
    const flowBarRef = useRef(null);
    const stepRefs = useRef({});
    const [showNavigation, setShowNavigation] = useState(false);
    const [isScrollStart, setIsScrollStart] = useState(true);
    const [isScrollEnd, setIsScrollEnd] = useState(false);

    const activeStatus = steps.findIndex((s) => s.id === value);

    const handleStepClick = (step) => {
        if (onChange) onChange(step.id);
    };

    const handleScroll = (direction) => {
        const bar = flowBarRef.current;
        if (!bar) return;
        const newScroll = bar.scrollLeft + (direction === 'prev' ? -200 : 200);
        bar.scrollTo({ left: newScroll, behavior: 'smooth' });
    };

    const onBarScroll = () => {
        const bar = flowBarRef.current;
        if (!bar) return;
        const atEnd = bar.scrollLeft + bar.offsetWidth >= bar.scrollWidth - 1;
        const atStart = bar.scrollLeft <= 1;
        setIsScrollEnd(atEnd);
        setIsScrollStart(atStart);
        bar.classList.toggle('end-border', !atEnd);
        bar.classList.toggle('start-border', !atStart);
    };

    const checkOverflow = () => {
        const bar = flowBarRef.current;
        if (!bar) return;
        setShowNavigation(bar.scrollWidth > bar.clientWidth);
        onBarScroll();
    };

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 600);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        const bar = flowBarRef.current;
        if (bar) bar.addEventListener('scroll', onBarScroll);
        return () => {
            window.removeEventListener('resize', checkOverflow);
            if (bar) bar.removeEventListener('scroll', onBarScroll);
        };
    }, []);

    useEffect(() => {
        checkOverflow();
    }, [steps]);

    useEffect(() => {
        if (!value) return;
        const el = stepRefs.current[value];
        if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, [value, steps]);

    const stepActions = steps.map((step) => ({
        text: step.name,
        type: step.id,
        class: value === step.id ? 'active' : '',
        onClick: () => handleStepClick(step),
    }));

    return (
        <div>
            {isMobile ? (
                <div className="recruitment-dropdown-wrap">
                    <ActionDropdown
                        actions={stepActions}
                        iconClass="icon-svg-chevron-down"
                        dropdownClass="recruitment-dropdown"
                        btnName={steps.find((s) => s.id === value)?.name || 'Select step'}
                    />
                </div>
            ) : (
                <div className="applicant-status-bar-group-wrapper">
                    <div className="recruitment-flow-slider-wrapper">
                        {showNavigation && (
                            <div
                                className={classNames('control left', { disabled: isScrollStart })}
                                onClick={() => handleScroll('prev')}
                            >
                                <Icon value="chevron-left" />
                            </div>
                        )}

                        <div className="recruitment-flow-slider" ref={flowBarRef}>
                            <div
                                className={classNames('recruitment-flow-slider-items', {
                                    'first-active': activeStatus === 0,
                                    'last-active': activeStatus === steps.length - 1,
                                })}
                            >
                                {steps.map((step, idx) => (
                                    <a
                                        key={step.id}
                                        ref={(el) => { stepRefs.current[step.id] = el; }}
                                        className={classNames({ active: idx === activeStatus })}
                                        data-step-id={step.id}
                                        onClick={() => handleStepClick(step)}
                                    >
                                        <span className="name">{step.name}</span>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {showNavigation && (
                            <div
                                className={classNames('control right', { disabled: isScrollEnd })}
                                onClick={() => handleScroll('next')}
                            >
                                <Icon value="chevron-right" />
                            </div>
                        )}
                    </div>

                    {showNavigation && (
                        <div className="flow-dots">
                            {steps.map((step, idx) => (
                                <div
                                    key={step.id}
                                    className={classNames('flow-dot', { active: idx === activeStatus })}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RecruitmentFlowBar;
